import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { usePageContent } from '@/context/SiteDataContext';
import { toYouTubeEmbed } from './BioPage';
import { Link } from 'react-router-dom';
import AdminHint from '@/components/editable/AdminHint';

/**
 * Legacy
 * ------
 * Roster of former / departed leaders. Data at page_content.legacy.legacy_leaders:
 *   [{ id, order, name, title, photo, bio, bio_paragraphs: [], youtube_url, show_video }]
 * Each leader gets an in-page card + collapsible bio + optional YouTube video.
 */
export default function Legacy() {
  const c = usePageContent('legacy');
  const leaders = (c.legacy_leaders || []).slice().sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
  return (
    <div>
      <section className="relative py-24 bg-royal-dark text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={c.hero_image || 'https://images.unsplash.com/photo-1519452575417-564c1401ecc0?w=2400'} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-royal/80 to-royal-dark/95" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <div className="overline text-gold mb-3">In Loving Memory</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold gold-underline inline-block">{c.hero_heading || 'Legacy of Faith'}</h1>
          <p className="mt-4 text-white/85 text-lg max-w-3xl mx-auto">
            {c.hero_blurb || 'Honoring the men and women of God who ran their race with faithfulness and now rest with the Lord.'}
          </p>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-6">
          {leaders.length === 0 && (
            <AdminHint className="text-center italic text-charcoal-muted py-16">
              No legacy leaders added yet. Add them in Admin ▸ Legacy.
            </AdminHint>
          )}
          <div className="space-y-16">
            {leaders.map((l, i) => (
              <LegacyCard key={l.id} leader={l} alt={i % 2 === 1} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function LegacyCard({ leader, alt }) {
  const paragraphs = (leader.bio_paragraphs && leader.bio_paragraphs.length)
    ? leader.bio_paragraphs
    : (leader.bio ? [leader.bio] : []);
  const showVideo = leader.show_video !== false && leader.youtube_url;
  const embed = showVideo ? toYouTubeEmbed(leader.youtube_url) : '';
  return (
    <div data-testid={`legacy-leader-${leader.id}`} className="grid md:grid-cols-12 gap-8 items-start">
      <div className={`md:col-span-4 ${alt ? 'md:order-2' : 'md:order-1'}`}>
        <div className="aspect-[3/4] rounded-xl overflow-hidden shadow-lg ring-4 ring-gold/20 bg-royal/10">
          {leader.photo && (
            <img src={leader.photo} alt={leader.name} className="w-full h-full object-cover"
              style={{
                objectPosition: leader.photo_position || 'center top',
                transform: leader.photo_scale ? `scale(${leader.photo_scale})` : undefined,
                transformOrigin: 'center top',
              }} />
          )}
        </div>
      </div>
      <div className={`md:col-span-8 ${alt ? 'md:order-1' : 'md:order-2'}`}>
        <div className="overline text-gold mb-2">{leader.title || 'Former Leader'}</div>
        <h2 className="font-serif text-3xl text-royal font-bold gold-underline">{leader.name}</h2>
        {leader.years && <div className="text-sm text-charcoal-muted mt-2 italic">{leader.years}</div>}
        <div className="mt-5 space-y-3 text-charcoal-muted leading-relaxed">
          {paragraphs.map((p, i) => <p key={i} className="whitespace-pre-wrap">{p}</p>)}
        </div>
        {embed && (
          <div className="mt-6" data-testid={`legacy-video-${leader.id}`}>
            <div className="aspect-video rounded-lg overflow-hidden shadow-lg ring-2 ring-gold/40 bg-black">
              <iframe src={embed} title={`${leader.name} video`} className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
